import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { showToast } from '@/lib/custom-toast';
import { 
  TrendingUp, TrendingDown, DollarSign, PieChart as PieChartIcon, 
  BarChart3, ArrowUpRight, ArrowDownRight, Target, Sparkles,
  Calendar, Award, Zap, Gem, Wallet, PiggyBank, ChevronRight, CheckCircle2, AlertTriangle,
  Eye, EyeOff
} from 'lucide-react';
import { formatCurrency } from '@/utils/currencyFormat';
import { useIsMobile } from '@/hooks/useIsMobile';
import { calculate503020Rule } from '@/utils/exportUtils';
import { 
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, 
  AreaChart, Area 
} from 'recharts';

const CATEGORY_COLORS = [
  '#6366f1', '#ec4899', '#10b981', '#f59e0b', '#3b82f6', 
  '#8b5cf6', '#14b8a6', '#f43f5e', '#64748b'
];

// Calculador de Salud Financiera Multidimensional Dinámico
const calculateFinancialHealth = (stats, transactions = []) => {
  const income = stats?.total_income || 0;
  const expenses = stats?.total_expenses || 0;
  const savings = stats?.total_savings || 0;

  // 1. Pilar de Flujo y Capacidad de Ahorro (Max 40 pts)
  let flowScore = 20;
  let flowNote = 'Sin movimientos de flujo en este período';
  if (income > 0) {
    const net = income - expenses;
    const savingsRate = net / income;
    if (savingsRate >= 0.20) {
      flowScore = 40;
      flowNote = 'Excelente tasa de retención y ahorro (>20%)';
    } else if (savingsRate >= 0.10) {
      flowScore = 32;
      flowNote = 'Buen ahorro del período (>10%)';
    } else if (savingsRate >= 0) {
      flowScore = 26;
      flowNote = 'Flujo positivo sin déficit';
    } else {
      const deficitRatio = Math.abs(net) / income;
      flowScore = Math.max(5, Math.round(20 - deficitRatio * 25));
      flowNote = 'Gastos superaron a ingresos en este período';
    }
  } else if (expenses > 0) {
    flowScore = 12;
    flowNote = 'Gastos sin ingresos registrados este período';
  } else if (savings > 0) {
    flowScore = 35;
    flowNote = 'Ahorros activos registrados';
  }

  // 2. Pilar de Diversificación y Control de Gastos (Max 35 pts)
  let expScore = 25;
  let expNote = 'Distribución de gastos equilibrada';
  if (stats?.by_category) {
    const expenseCats = Object.values(stats.by_category).filter(c => c.type === 'expense' && c.total > 0);
    if (expenseCats.length > 0 && expenses > 0) {
      const highestCat = Math.max(...expenseCats.map(c => c.total));
      const concentration = highestCat / expenses;
      if (concentration > 0.70) {
        expScore = 18;
        expNote = 'Alta concentración en un solo rubro de gasto';
      } else {
        expScore = 35;
        expNote = 'Gastos balanceados entre categorías';
      }
    }
  }

  // 3. Pilar de Hábitos y Ahorro Acumulado (Max 25 pts)
  let habitScore = 18;
  let habitNote = 'Hábitos financieros estables';
  if (savings > 0 || (stats?.total_savings > 0)) {
    habitScore = 25;
    habitNote = 'Hábito de ahorro activo y constante';
  } else if (transactions.length >= 5) {
    habitScore = 22;
    habitNote = 'Registro constante de movimientos';
  }

  const totalScore = Math.min(100, Math.max(20, flowScore + expScore + habitScore));
  return {
    score: totalScore,
    notes: [flowNote, expNote, habitNote].filter(Boolean)
  };
};

// Vista Móvil Fintech Premium para la pestaña Estadísticas
const MobileStatsView = ({ stats, transactions, selectedPeriod, setSelectedPeriod, availableMonths, totalSavings, hideBalances, toggleHideBalances }) => {
  const healthData = useMemo(() => {
    return calculateFinancialHealth(stats, transactions);
  }, [stats, transactions]);

  const score = healthData.score;

  const getScoreInfo = (s) => {
    if (s >= 80) return { label: 'Salud Financiera Excelente', color: 'text-emerald-400', stroke: '#34d399', bg: 'bg-emerald-500/10' };
    if (s >= 60) return { label: 'Salud Financiera Buena', color: 'text-amber-400', stroke: '#fbbf24', bg: 'bg-amber-500/10' };
    return { label: 'Requiere Atención', color: 'text-rose-400', stroke: '#f87171', bg: 'bg-rose-500/10' };
  };

  const scoreInfo = getScoreInfo(score);

  const categoryList = useMemo(() => {
    if (!stats?.by_category) return [];
    return Object.values(stats.by_category)
      .filter(c => c.type === 'expense' && c.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [stats]);

  const totalExpense = useMemo(() => {
    return categoryList.reduce((acc, c) => acc + c.total, 0);
  }, [categoryList]);

  const areaData = useMemo(() => {
    if (!transactions.length) return [];
    const sorted = [...transactions].sort((a, b) => new Date(a.date) - new Date(b.date));
    let acc = 0;
    return sorted.slice(-10).map(t => {
      acc += t.type === 'income' ? Number(t.amount || 0) : -Number(t.amount || 0);
      return {
        date: new Date(t.date).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }),
        val: acc
      };
    });
  }, [transactions]);

  const netBalance = (stats?.total_income || 0) - (stats?.total_expenses || 0);

  return (
    <div className="space-y-5 pb-12 animate-fadeIn">
      {/* Selector Único de Mes */}
      {(() => {
        const currentDate = new Date();
        const prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        const previousMonthKey = `${prevDate.getFullYear()}-${prevDate.getMonth() + 1}`;

        return (
          <div className="flex items-center justify-between bg-white border border-slate-200/80 p-2 px-3 rounded-2xl shadow-sm gap-2">
            <div className="flex items-center gap-2 text-slate-700 font-bold text-xs shrink-0">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span>Período:</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleHideBalances}
                className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
                title={hideBalances ? "Mostrar cantidades" : "Ocultar cantidades"}
              >
                {hideBalances ? <EyeOff className="w-4 h-4 text-slate-700" /> : <Eye className="w-4 h-4 text-slate-700" />}
              </button>
              <div className="relative">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="appearance-none bg-slate-100 text-slate-900 font-bold text-xs px-3 py-1.5 pr-7 rounded-xl border border-slate-200 focus:outline-none cursor-pointer"
                >
                  <option value="current">Este mes</option>
                  <option value={previousMonthKey}>Mes pasado</option>
                  <option value="all">Todo el tiempo</option>
                  {availableMonths
                    .filter(m => m.value !== `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}` && m.value !== previousMonthKey)
                    .map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                </select>
                <ChevronRight className="w-3.5 h-3.5 text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Diagnóstico Financiero - Tarjeta Limpia en Blanco */}
      <Card className="p-4 bg-white border-slate-100 shadow-sm rounded-3xl space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Salud Financiera</p>
          <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ${scoreInfo.bg} ${scoreInfo.color}`}>
            {scoreInfo.label}
          </span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
              <path className="text-slate-100" strokeWidth="3.5" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              <path strokeWidth="3.5" strokeDasharray={`${score}, 100`} strokeLinecap="round" stroke={scoreInfo.stroke} fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            </svg>
            <span className="absolute text-lg font-black text-slate-900">{score}</span>
          </div>

          <div className="space-y-1 min-w-0 flex-1">
            <p className="text-xs font-bold text-slate-800">
              {healthData.notes[0] || 'Diagnóstico del período'}
            </p>
            <p className="text-[11px] text-slate-500 leading-snug">
              {healthData.notes[1] || 'Evaluación de flujo, ahorros y hábitos.'}
            </p>
          </div>
        </div>
      </Card>

      {/* Grid 2x2 KPIs Compactos */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-2">
          <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <ArrowUpRight className="w-4 h-4" />
          </div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Ingresos</p>
          <p className="text-base font-black text-slate-900">
            {hideBalances ? '••••••' : formatCurrency(stats?.total_income || 0)}
          </p>
        </div>

        <div className="p-4 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-2">
          <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <ArrowDownRight className="w-4 h-4" />
          </div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Gastos</p>
          <p className="text-base font-black text-slate-900">
            {hideBalances ? '••••••' : formatCurrency(stats?.total_expenses || 0)}
          </p>
        </div>

        <div className="p-4 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-2">
          <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <PiggyBank className="w-4 h-4" />
          </div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Ahorros</p>
          <p className="text-base font-black text-slate-900">
            {hideBalances ? '••••••' : formatCurrency(totalSavings || 0)}
          </p>
        </div>

        <div className="p-4 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-2">
          <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Wallet className="w-4 h-4" />
          </div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Balance Neto</p>
          <p className={`text-base font-black ${netBalance < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
            {hideBalances ? '••••••' : formatCurrency(netBalance)}
          </p>
        </div>
      </div>

      {/* Tendencia Área Chart */}
      {areaData.length > 0 && (
        <Card className="p-4 bg-white border-slate-100 shadow-sm rounded-3xl space-y-3">
          <div className="flex items-center justify-between px-1">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Tendencia de Flujo</h3>
              <p className="text-[10px] text-slate-400 font-medium">Toca o desliza para ver valores por fecha</p>
            </div>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
              Últimos {areaData.length} movs
            </span>
          </div>

          {/* Badges de referencia rápida */}
          <div className="flex items-center justify-between bg-slate-50 p-2 px-3 rounded-2xl border border-slate-100 text-xs">
            <div>
              <span className="text-[10px] text-slate-400 block uppercase font-bold">Inicio</span>
              <span className="font-bold text-slate-700">{formatCurrency(areaData[0]?.val || 0)}</span>
            </div>
            <div className="text-right">
              <span className="text-[10px] text-slate-400 block uppercase font-bold">Actual</span>
              <span className={`font-bold ${(areaData[areaData.length - 1]?.val || 0) >= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>
                {formatCurrency(areaData[areaData.length - 1]?.val || 0)}
              </span>
            </div>
          </div>

          <div className="h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={areaData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorNetStatsMobile" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} dy={3} />
                <Tooltip formatter={(val) => [formatCurrency(val), 'Flujo Acumulado']} contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                <Area 
                  type="monotone" 
                  dataKey="val" 
                  stroke="#6366f1" 
                  strokeWidth={2.5} 
                  fillOpacity={1} 
                  fill="url(#colorNetStatsMobile)"
                  dot={{ r: 4, fill: '#6366f1', stroke: '#ffffff', strokeWidth: 2 }}
                  activeDot={{ r: 7, fill: '#4f46e5', stroke: '#ffffff', strokeWidth: 3 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Categorías en Filas Compactas */}
      {categoryList.length > 0 && (
        <Card className="p-5 bg-white border-slate-100 shadow-sm rounded-3xl space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900">Top Gastos por Categoría</h3>
            <span className="text-[10px] font-bold text-slate-400">{categoryList.length} activas</span>
          </div>

          <div className="space-y-3">
            {categoryList.map((cat, idx) => {
              const pct = totalExpense > 0 ? ((cat.total / totalExpense) * 100).toFixed(1) : 0;
              const color = cat.color || CATEGORY_COLORS[idx % CATEGORY_COLORS.length];

              return (
                <div key={cat.name} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-bold">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-slate-800">{cat.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 text-[10px]">{pct}%</span>
                      <span className="text-slate-900 font-black">{formatCurrency(cat.total)}</span>
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

const Statistics = () => {
  const { token } = useContext(AuthContext);
  const isMobile = useIsMobile();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [animateCards, setAnimateCards] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [availableMonths, setAvailableMonths] = useState([]);

  const [hideBalances, setHideBalances] = useState(() => {
    try { return localStorage.getItem('hideBalances') === 'true'; } catch { return false; }
  });

  const toggleHideBalances = () => {
    const nextVal = !hideBalances;
    setHideBalances(nextVal);
    try {
      localStorage.setItem('hideBalances', String(nextVal));
      window.dispatchEvent(new Event('storage'));
    } catch {}
  };

  useEffect(() => {
    const handleStorage = () => {
      try { setHideBalances(localStorage.getItem('hideBalances') === 'true'); } catch {}
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      let url = `${API}/statistics`;
      const currentDate = new Date();
      if (selectedPeriod === 'current') {
        url += `?month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}`;
      } else if (selectedPeriod !== 'all') {
        const [year, month] = selectedPeriod.split('-');
        url += `?month=${month}&year=${year}`;
      }

      const [statsRes, transRes, accsRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats(statsRes.data);
      setTransactions(transRes.data);
      setAccounts(accsRes.data || []);

      // Extraer meses disponibles
      const monthsSet = new Set();
      transRes.data.forEach(t => {
        const d = new Date(t.date);
        if (!isNaN(d.getTime())) {
          monthsSet.add(`${d.getFullYear()}-${d.getMonth() + 1}`);
        }
      });

      const monthsArray = Array.from(monthsSet).sort((a, b) => {
        const [yA, mA] = a.split('-').map(Number);
        const [yB, mB] = b.split('-').map(Number);
        return yB !== yA ? yB - yA : mB - mA;
      });

      const formatted = monthsArray.map(mKey => {
        const [y, m] = mKey.split('-').map(Number);
        const d = new Date(y, m - 1, 1);
        const name = d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
        return { value: mKey, label: name.charAt(0).toUpperCase() + name.slice(1) };
      });

      setAvailableMonths(formatted);
    } catch (error) {
      showToast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  }, [token, selectedPeriod]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Listener para refrescar estadísticas automáticamente en vivo
  useEffect(() => {
    const handleTransactionAdded = () => {
      fetchStats();
    };
    window.addEventListener('transaction-added', handleTransactionAdded);
    return () => window.removeEventListener('transaction-added', handleTransactionAdded);
  }, [fetchStats]);

  useEffect(() => {
    if (stats) {
      setTimeout(() => setAnimateCards(true), 100);
    }
  }, [stats]);


  const periodTransactions = useMemo(() => {
    if (!transactions.length) return [];
    if (selectedPeriod === 'all') return transactions;
    const currentDate = new Date();
    let targetYear = currentDate.getFullYear();
    let targetMonth = currentDate.getMonth() + 1;
    if (selectedPeriod !== 'current') {
      const [y, m] = selectedPeriod.split('-');
      targetYear = parseInt(y, 10);
      targetMonth = parseInt(m, 10);
    }
    return transactions.filter(t => {
      const d = new Date(t.date);
      return d.getFullYear() === targetYear && (d.getMonth() + 1) === targetMonth;
    });
  }, [transactions, selectedPeriod]);

  const savingsAssets = (accounts || []).reduce((sum, a) => {
    if (a.type === 'credit_card' || a.is_active === false || !a.has_yield) return sum;
    const amt = a.currency === 'USD' ? (a.current_balance || 0) * 17 : (a.current_balance || 0);
    return sum + amt;
  }, 0);
  const totalSavings = (stats?.total_savings || 0) + savingsAssets;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-lg text-slate-600">Cargando estadísticas...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (isMobile) {
    return (
      <Layout>
        <MobileStatsView
          stats={stats}
          transactions={periodTransactions}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          availableMonths={availableMonths}
          totalSavings={totalSavings}
          hideBalances={hideBalances}
          toggleHideBalances={toggleHideBalances}
        />
      </Layout>
    );
  }

  const categoryData = Object.entries(stats?.by_category || {}).map(([id, data]) => ({
    id,
    name: data.name,
    value: data.total,
    color: data.color,
    count: data.count,
    type: data.type
  }));

  const incomeCategories = categoryData.filter(c => c.type === 'income' && c.value > 0);
  const expenseCategories = categoryData.filter(c => c.type === 'expense' && c.value > 0);
  const savingCategories = categoryData.filter(c => c.type === 'saving' && c.value > 0);

  // Sort categories by value descending
  const topExpenseCategories = [...expenseCategories].sort((a, b) => b.value - a.value).slice(0, 5);
  const topIncomeCategories = [...incomeCategories].sort((a, b) => b.value - a.value).slice(0, 3);

  const hasData = stats?.total_income > 0 || stats?.total_expenses > 0 || stats?.total_savings > 0;
  const balance = (stats?.total_income || 0) - (stats?.total_expenses || 0);
  const savingsRate = stats?.total_income > 0 ? ((stats?.total_savings || 0) / stats.total_income) * 100 : 0;

  // Calculate percentages for animated pie chart
  const calculatePercentages = (categories) => {
    const total = categories.reduce((sum, cat) => sum + cat.value, 0);
    let currentAngle = 0;
    return categories.map(cat => {
      const percentage = (cat.value / total) * 100;
      const angle = (percentage / 100) * 360;
      const result = {
        ...cat,
        percentage,
        startAngle: currentAngle,
        endAngle: currentAngle + angle
      };
      currentAngle += angle;
      return result;
    });
  };

  const AnimatedPieChart = ({ data, size = 250 }) => {
    if (!data || data.length === 0) return null;
    
    const dataWithAngles = calculatePercentages(data);
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = (size / 2) - 20;
    const innerRadius = radius * 0.6; // For donut chart

    const polarToCartesian = (angle, r = radius) => {
      const rad = (angle - 90) * Math.PI / 180;
      return {
        x: centerX + r * Math.cos(rad),
        y: centerY + r * Math.sin(rad)
      };
    };

    const createDonutArc = (start, end) => {
      if (end - start >= 359.99) {
        return `M ${centerX} ${centerY - radius} 
                A ${radius} ${radius} 0 1 1 ${centerX} ${centerY + radius} 
                A ${radius} ${radius} 0 1 1 ${centerX} ${centerY - radius} 
                M ${centerX} ${centerY - innerRadius} 
                A ${innerRadius} ${innerRadius} 0 1 0 ${centerX} ${centerY + innerRadius} 
                A ${innerRadius} ${innerRadius} 0 1 0 ${centerX} ${centerY - innerRadius} Z`;
      }
      
      const outerStart = polarToCartesian(start, radius);
      const outerEnd = polarToCartesian(end, radius);
      const innerStart = polarToCartesian(start, innerRadius);
      const innerEnd = polarToCartesian(end, innerRadius);
      const largeArc = end - start > 180 ? 1 : 0;
      
      return `M ${outerStart.x} ${outerStart.y} 
              A ${radius} ${radius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y} 
              L ${innerEnd.x} ${innerEnd.y} 
              A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y} Z`;
    };

    return (
      <div className="relative">
        <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-auto transform hover:scale-105 transition-transform duration-300">
          {dataWithAngles.map((item, index) => (
            <g key={index} className="cursor-pointer hover:opacity-80 transition-opacity">
              <path
                d={createDonutArc(item.startAngle, item.endAngle)}
                fill={item.color}
                stroke="white"
                strokeWidth="3"
                style={{
                  animation: `fadeIn 0.6s ease-out ${index * 0.1}s both`
                }}
              />
            </g>
          ))}
          {/* Center text */}
          <text 
            x={centerX} 
            y={centerY - 5} 
            textAnchor="middle" 
            className="text-2xl font-bold fill-slate-900"
          >
            {dataWithAngles.length}
          </text>
          <text 
            x={centerX} 
            y={centerY + 15} 
            textAnchor="middle" 
            className="text-sm fill-slate-600"
          >
            Categorías
          </text>
        </svg>
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}</style>
      </div>
    );
  };

  const TopCategoriesBar = ({ categories, type }) => {
    const maxValue = Math.max(...categories.map(c => c.value));
    const colors = {
      expense: { bg: 'from-red-500 to-rose-600', light: 'bg-red-50', border: 'border-red-200' },
      income: { bg: 'from-green-500 to-emerald-600', light: 'bg-green-50', border: 'border-green-200' },
      saving: { bg: 'from-blue-500 to-cyan-600', light: 'bg-blue-50', border: 'border-blue-200' }
    };

    return (
      <div className="space-y-4">
        {categories.map((category, index) => {
          const percentage = (category.value / maxValue) * 100;
          return (
            <div 
              key={index} 
              className={`p-4 rounded-xl ${colors[type].light} border ${colors[type].border} transform hover:scale-102 transition-all duration-300`}
              style={{
                animation: `slideInRight 0.5s ease-out ${index * 0.1}s both`
              }}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="font-semibold text-slate-900">{category.name}</span>
                </div>
                <span className="font-bold text-lg text-slate-900">{formatCurrency(category.value)}</span>
              </div>
              <div className="w-full bg-white/50 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${colors[type].bg} rounded-full transition-all duration-1000 ease-out flex items-center justify-end px-2`}
                  style={{
                    width: animateCards ? `${percentage}%` : '0%'
                  }}
                >
                  {percentage > 20 && (
                    <span className="text-xs font-bold text-white">{percentage.toFixed(0)}%</span>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-1">{category.count} transacciones</p>
            </div>
          );
        })}
        <style>{`
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
        `}</style>
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
        {/* Header con Selector de Mes */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-600" />
              Estadísticas Financieras
            </h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">Análisis visual y detallado de tus finanzas</p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleHideBalances}
              className="p-2.5 bg-white border border-slate-200 rounded-2xl hover:bg-slate-50 transition-colors shadow-sm text-slate-600 flex items-center justify-center"
              title={hideBalances ? "Mostrar cantidades" : "Ocultar cantidades"}
            >
              {hideBalances ? <EyeOff className="w-5 h-5 text-slate-700" /> : <Eye className="w-5 h-5 text-slate-700" />}
            </button>
            <div className="flex items-center gap-3 bg-white border border-slate-200 p-2 px-4 rounded-2xl shadow-sm">
              <Calendar className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-bold text-slate-700">Período:</span>
            {(() => {
              const currentDate = new Date();
              const prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
              const previousMonthKey = `${prevDate.getFullYear()}-${prevDate.getMonth() + 1}`;

              return (
                <div className="relative">
                  <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="appearance-none bg-slate-100 text-slate-900 font-bold text-sm px-3 py-1.5 pr-8 rounded-xl border border-slate-200 focus:outline-none cursor-pointer"
                  >
                    <option value="current">Este mes</option>
                    <option value={previousMonthKey}>Mes pasado</option>
                    <option value="all">Todo el tiempo</option>
                    {availableMonths
                      .filter(m => m.value !== `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}` && m.value !== previousMonthKey)
                      .map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                  </select>
                  <ChevronRight className="w-4 h-4 text-slate-500 absolute right-2.5 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" />
                </div>
              );
            })()}
            </div>
          </div>
        </div>

        {/* Tarjeta de Salud Financiera Desktop */}
        {(() => {
          const healthData = calculateFinancialHealth(stats, periodTransactions);
          const score = healthData.score;
          const getScoreInfo = (s) => {
            if (s >= 80) return { label: 'Salud Financiera Excelente', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' };
            if (s >= 60) return { label: 'Salud Financiera Buena', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' };
            return { label: 'Requiere Atención', color: 'text-rose-600', bg: 'bg-rose-50 border-rose-200' };
          };
          const info = getScoreInfo(score);

          return (
            <Card className={`p-6 bg-white border ${info.bg} rounded-3xl shadow-sm flex items-center justify-between gap-6`}>
              <div className="flex items-center gap-6">
                <div className="relative w-20 h-20 flex items-center justify-center shrink-0">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    <path className="text-slate-100" strokeWidth="3.5" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    <path strokeWidth="3.5" strokeDasharray={`${score}, 100`} strokeLinecap="round" stroke="currentColor" className={info.color} fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  </svg>
                  <span className="absolute text-xl font-black text-slate-900">{score}</span>
                </div>
                <div>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${info.color} ${info.bg} mb-1`}>
                    {info.label}
                  </span>
                  <h3 className="text-lg font-bold text-slate-900">Diagnóstico del Período</h3>
                  <p className="text-sm text-slate-700 font-semibold mt-1">
                    {healthData.notes[0] || 'Evaluación integral de hábitos financieros.'}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5 max-w-xl">
                    {healthData.notes.slice(1).join(' • ') || 'Basado en capacidad de ahorro, balance de flujo y diversificación.'}
                  </p>
                </div>
              </div>
              <Sparkles className="w-10 h-10 text-amber-500 shrink-0 hidden md:block" />
            </Card>
          );
        })()}

        {!hasData ? (
          <Card className="p-12 bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <div className="text-center">
              <PieChartIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <p className="text-xl text-slate-600 mb-2 font-semibold">
                No hay datos suficientes para mostrar estadísticas
              </p>
              <p className="text-slate-500">
                Comienza agregando transacciones para ver análisis detallados de tus finanzas
              </p>
            </div>
          </Card>
        ) : (
          <>
            {/* Main Summary Cards - Animated */}
            <div className="grid md:grid-cols-4 gap-6">
              {/* Ingresos */}
              <Card className="p-6 bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <ArrowUpRight className="w-5 h-5 opacity-70" />
                </div>
                <p className="text-sm text-green-100 font-medium mb-1">Ingresos Totales</p>
                <p className="text-3xl font-bold mb-2">
                  {hideBalances ? '••••••' : formatCurrency(stats?.total_income)}
                </p>
                <div className="flex items-center gap-1 text-xs text-green-100">
                  <Award className="w-3 h-3" />
                  <span>{incomeCategories.length} categorías</span>
                </div>
              </Card>

              {/* Gastos */}
              <Card className="p-6 bg-gradient-to-br from-red-500 to-rose-600 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <TrendingDown className="w-6 h-6" />
                  </div>
                  <ArrowDownRight className="w-5 h-5 opacity-70" />
                </div>
                <p className="text-sm text-red-100 font-medium mb-1">Gastos Totales</p>
                <p className="text-3xl font-bold mb-2">
                  {hideBalances ? '••••••' : formatCurrency(stats?.total_expenses)}
                </p>
                <div className="flex items-center gap-1 text-xs text-red-100">
                  <Award className="w-3 h-3" />
                  <span>{expenseCategories.length} categorías</span>
                </div>
              </Card>

              {/* Ahorros */}
              <Card className="p-6 bg-gradient-to-br from-blue-500 to-cyan-600 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Target className="w-6 h-6" />
                  </div>
                  <Zap className="w-5 h-5 opacity-70" />
                </div>
                <p className="text-sm text-blue-100 font-medium mb-1">Ahorros Totales</p>
                <p className="text-3xl font-bold mb-2">
                  {hideBalances ? '••••••' : formatCurrency(totalSavings)}
                </p>
                <div className="flex items-center gap-1 text-xs text-blue-100">
                  <Award className="w-3 h-3" />
                  <span>{savingsRate.toFixed(1)}% de ingresos</span>
                </div>
              </Card>

              {/* Balance */}
              <Card className={`p-6 bg-gradient-to-br ${balance >= 0 ? 'from-purple-500 to-pink-600' : 'from-orange-500 to-red-600'} text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  {balance >= 0 ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                </div>
                <p className="text-sm text-white/90 font-medium mb-1">Balance Neto</p>
                <p className="text-3xl font-bold mb-2">
                  {hideBalances ? '••••••' : formatCurrency(balance)}
                </p>
                <div className="flex items-center gap-1 text-xs text-white/90">
                  <Calendar className="w-3 h-3" />
                  <span>{balance >= 0 ? 'Superávit' : 'Déficit'}</span>
                </div>
              </Card>
            </div>

            {/* Gráfica de Tendencia de Flujo Desktop */}
            {(() => {
              const sorted = [...transactions].sort((a, b) => new Date(a.date) - new Date(b.date));
              let acc = 0;
              const desktopAreaData = sorted.slice(-15).map(t => {
                acc += t.type === 'income' ? Number(t.amount || 0) : -Number(t.amount || 0);
                return {
                  date: new Date(t.date).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }),
                  val: acc
                };
              });

              if (desktopAreaData.length === 0) return null;

              return (
                <Card className="p-8 bg-white/90 backdrop-blur-sm border-slate-200 shadow-xl rounded-3xl space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                        <BarChart3 className="w-6 h-6 text-indigo-600" />
                        Tendencia de Flujo Financiero
                      </h2>
                      <p className="text-sm text-slate-600 mt-1">Comportamiento acumulado de tus ingresos y gastos recientes</p>
                    </div>
                    <div className="flex items-center gap-4 bg-slate-50 p-3 px-5 rounded-2xl border border-slate-100">
                      <div>
                        <span className="text-xs text-slate-400 block uppercase font-bold">Inicio</span>
                        <span className="text-base font-bold text-slate-800">{formatCurrency(desktopAreaData[0]?.val || 0)}</span>
                      </div>
                      <div className="h-8 w-px bg-slate-200" />
                      <div>
                        <span className="text-xs text-slate-400 block uppercase font-bold">Flujo Actual</span>
                        <span className={`text-base font-black ${(desktopAreaData[desktopAreaData.length - 1]?.val || 0) >= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>
                          {formatCurrency(desktopAreaData[desktopAreaData.length - 1]?.val || 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="h-64 w-full pt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={desktopAreaData} margin={{ top: 15, right: 20, left: 20, bottom: 10 }}>
                        <defs>
                          <linearGradient id="colorNetStatsDesktop" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02}/>
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={5} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(val) => `$${val}`} />
                        <Tooltip formatter={(val) => [formatCurrency(val), 'Flujo Acumulado']} contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                        <Area 
                          type="monotone" 
                          dataKey="val" 
                          stroke="#6366f1" 
                          strokeWidth={3} 
                          fillOpacity={1} 
                          fill="url(#colorNetStatsDesktop)"
                          dot={{ r: 5, fill: '#6366f1', stroke: '#ffffff', strokeWidth: 2 }}
                          activeDot={{ r: 8, fill: '#4f46e5', stroke: '#ffffff', strokeWidth: 3 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              );
            })()}

            {/* Charts Grid */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Top Expense Categories */}
              {topExpenseCategories.length > 0 && (
                <Card className="p-8 bg-white/90 backdrop-blur-sm border-slate-200 shadow-xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl">
                      <TrendingDown className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Top Gastos</h2>
                      <p className="text-sm text-slate-600">Categorías con mayor gasto</p>
                    </div>
                  </div>
                  <TopCategoriesBar categories={topExpenseCategories} type="expense" />
                </Card>
              )}

              {/* Top Income Categories */}
              {topIncomeCategories.length > 0 && (
                <Card className="p-8 bg-white/90 backdrop-blur-sm border-slate-200 shadow-xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Top Ingresos</h2>
                      <p className="text-sm text-slate-600">Fuentes principales de ingresos</p>
                    </div>
                  </div>
                  <TopCategoriesBar categories={topIncomeCategories} type="income" />
                </Card>
              )}
            </div>

            {/* Pie Charts Section */}
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Expense Distribution */}
              {expenseCategories.length > 0 && (
                <Card className="p-8 bg-gradient-to-br from-red-50 to-rose-50 border-red-200 shadow-xl">
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <PieChartIcon className="w-6 h-6 text-red-600" />
                    Distribución de Gastos
                  </h3>
                  <AnimatedPieChart data={expenseCategories} />
                  <div className="mt-6 space-y-2">
                    {expenseCategories.slice(0, 3).map((cat, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          <span className="text-slate-700">{cat.name}</span>
                        </div>
                        <span className="font-semibold text-slate-900">{((cat.value / stats.total_expenses) * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Income Distribution */}
              {incomeCategories.length > 0 && (
                <Card className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-xl">
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <PieChartIcon className="w-6 h-6 text-green-600" />
                    Distribución de Ingresos
                  </h3>
                  <AnimatedPieChart data={incomeCategories} />
                  <div className="mt-6 space-y-2">
                    {incomeCategories.slice(0, 3).map((cat, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          <span className="text-slate-700">{cat.name}</span>
                        </div>
                        <span className="font-semibold text-slate-900">{((cat.value / stats.total_income) * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Savings Distribution */}
              {savingCategories.length > 0 && (
                <Card className="p-8 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200 shadow-xl">
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <PieChartIcon className="w-6 h-6 text-blue-600" />
                    Distribución de Ahorros
                  </h3>
                  <AnimatedPieChart data={savingCategories} />
                  <div className="mt-6 space-y-2">
                    {savingCategories.map((cat, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          <span className="text-slate-700">{cat.name}</span>
                        </div>
                        <span className="font-semibold text-slate-900">{((cat.value / stats.total_savings) * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Statistics;
